/**
 * 定时器管理 Composable
 * 统一管理所有定时器的创建和清理
 */

import config from '@/config';
import logger from '@/utils/logger';
import screenLifecycleStats from '@/utils/screenLifecycleStats';

export default function useTimers() {
  return {
    data() {
      return {
        progressTimer: null,
        dataRefreshTimer: null,
        rotationTimer: null,
        timeUpdateTimer: null,
        welcomeMessageTimer: null,
        realtimeTimer: null,
        sleepStateTimer: null,
        chartUpdateTimer: null,
        bubbleTimer: null,
        brainwaveDisplayTimer: null,
        videoTimer: null,
        videoPreloadTimer: null,
        hourlyRecoveryTimer: null,
        hourlyRecoveryStarter: null,
        midnightResetTimer: null,
        currentTimeValue: '', // 存储当前时间值
        fetchAppointmentDataRetryTimer: null, // fetchAppointmentData 等轮播结束再刷新的第一层延迟
        fetchAppointmentDataRetryTimer2: null, // 第二层 500ms 延迟
        fetchAppointmentDataDelayTimer: null, // 轮播刚结束 1.5 秒内延迟刷新
      };
    },

    methods: {
      /**
       * 启动时间更新定时器
       */
      startTimeUpdateTimer() {
        if (this.timeUpdateTimer) {
          clearInterval(this.timeUpdateTimer);
          screenLifecycleStats.recordTimerClear('interval', 'timeUpdateTimer');
        }
        screenLifecycleStats.recordTimerCreate('interval', 'timeUpdateTimer');
        this.timeUpdateTimer = setInterval(() => {
          const now = new Date();
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          this.currentTimeValue = `${hours}:${minutes}:${seconds}`;
        }, 1000);
      },

      /**
       * 初始化所有定时器
       */
      initAllTimers() {
        const performanceConfig = config.performance || {};
        const chartUpdateMs = Number(performanceConfig.chartUpdateMs) || 2000;
        const brainwaveDisplayMs = Number(performanceConfig.brainwaveDisplayMs) || 1000;
        const bubbleUpdateMs = Number(performanceConfig.bubbleUpdateMs);

        // 清理所有定时器
        if (this.progressTimer) {
          clearInterval(this.progressTimer);
          screenLifecycleStats.recordTimerClear('interval', 'progressTimer');
        }
        if (this.dataRefreshTimer) {
          clearInterval(this.dataRefreshTimer);
          screenLifecycleStats.recordTimerClear('interval', 'dataRefreshTimer');
        }
        if (this.chartUpdateTimer) {
          clearInterval(this.chartUpdateTimer);
          screenLifecycleStats.recordTimerClear('interval', 'chartUpdateTimer');
        }
        if (this.bubbleTimer) {
          clearInterval(this.bubbleTimer);
          screenLifecycleStats.recordTimerClear('interval', 'bubbleTimer');
        }
        if (this.welcomeMessageTimer) {
          clearInterval(this.welcomeMessageTimer);
          screenLifecycleStats.recordTimerClear('interval', 'welcomeMessageTimer');
        }
        if (this.realtimeTimer) {
          clearInterval(this.realtimeTimer);
          screenLifecycleStats.recordTimerClear('interval', 'realtimeTimer');
        }
        if (this.welcomeAnimationTimer) {
          clearTimeout(this.welcomeAnimationTimer);
          screenLifecycleStats.recordTimerClear('timeout', 'welcomeAnimationTimer');
        }
        if (this.videoTimer) {
          clearInterval(this.videoTimer);
          screenLifecycleStats.recordTimerClear('interval', 'videoTimer');
        }

        screenLifecycleStats.recordTimerCreate('interval', 'progressTimer');
        this.progressTimer = setInterval(() => {
          if (this.checkUpcomingAppointments) {
            this.checkUpcomingAppointments();
          }
        }, 1000);

        screenLifecycleStats.recordTimerCreate('interval', 'dataRefreshTimer');
        this.dataRefreshTimer = setInterval(() => {
          if (this.fetchAppointmentData) {
            this.fetchAppointmentData();
          }
          if (this.fetchTodayAppointmentData) {
            this.fetchTodayAppointmentData();
          }
        }, 30 * 1000);

        screenLifecycleStats.recordTimerCreate('interval', 'welcomeMessageTimer');
        this.welcomeMessageTimer = setInterval(() => {
          if (this.fetchWelcomeMessage) {
            this.fetchWelcomeMessage();
          }
        }, 20 * 1000);

        screenLifecycleStats.recordTimerCreate('interval', 'videoTimer');
        this.videoTimer = setInterval(() => {
          if (this.fetchVideoList) {
            this.fetchVideoList();
          }
        }, 60 * 1000);

        screenLifecycleStats.recordTimerCreate('interval', 'realtimeTimer');
        this.realtimeTimer = setInterval(() => {
          if (this.fetchRealtimeData && typeof this.fetchRealtimeData === 'function') {
            this.fetchRealtimeData();
          }
        }, 10 * 1000);

        screenLifecycleStats.recordTimerCreate('interval', 'chartUpdateTimer');
        this.chartUpdateTimer = setInterval(() => {
          // 方案甲：虚拟脑波 waveData 仅由此定时器写入，updateAllCharts 只负责渲染
          if (this.updateVirtualBrainwaveData && typeof this.updateVirtualBrainwaveData === 'function') {
            try {
              this.updateVirtualBrainwaveData();
            } catch (error) {
              logger.error('updateVirtualBrainwaveData error:', error);
            }
          }
          if (this.updateAllCharts) {
            this.updateAllCharts();
          }
        }, chartUpdateMs);

        this.$nextTick(() => {
          if (this.brainwaveDisplayTimer) {
            clearInterval(this.brainwaveDisplayTimer);
            screenLifecycleStats.recordTimerClear('interval', 'brainwaveDisplayTimer');
          }
          screenLifecycleStats.recordTimerCreate('interval', 'brainwaveDisplayTimer');
          this.brainwaveDisplayTimer = setInterval(() => {
            if (this && this.updateBrainwaveDisplay && typeof this.updateBrainwaveDisplay === 'function') {
              try {
                this.updateBrainwaveDisplay();
              } catch (error) {
                logger.error('updateBrainwaveDisplay error:', error);
              }
            }
          }, brainwaveDisplayMs);
        });

        // 初始化气泡更新定时器（配置为0时关闭）
        if (bubbleUpdateMs > 0) {
          screenLifecycleStats.recordTimerCreate('interval', 'bubbleTimer');
          this.bubbleTimer = setInterval(() => {
            if (this.updateBubbles) {
              this.updateBubbles();
            }
          }, bubbleUpdateMs);
        } else {
          this.bubbleTimer = null;
        }

        // 启动时间更新定时器
        this.startTimeUpdateTimer();
      },

      /**
       * 清理所有定时器
       */
      clearAllTimers() {
        if (this.progressTimer) {
          clearInterval(this.progressTimer);
          screenLifecycleStats.recordTimerClear('interval', 'progressTimer');
          this.progressTimer = null;
        }
        if (this.dataRefreshTimer) {
          clearInterval(this.dataRefreshTimer);
          screenLifecycleStats.recordTimerClear('interval', 'dataRefreshTimer');
          this.dataRefreshTimer = null;
        }
        if (this.rotationTimer) {
          clearTimeout(this.rotationTimer);
          clearInterval(this.rotationTimer);
          screenLifecycleStats.recordTimerClear('timeout', 'rotationTimer');
          screenLifecycleStats.recordTimerClear('interval', 'rotationTimer');
          this.rotationTimer = null;
        }
        if (this.chartUpdateTimer) {
          clearInterval(this.chartUpdateTimer);
          screenLifecycleStats.recordTimerClear('interval', 'chartUpdateTimer');
          this.chartUpdateTimer = null;
        }
        if (this.bubbleTimer) {
          clearInterval(this.bubbleTimer);
          screenLifecycleStats.recordTimerClear('interval', 'bubbleTimer');
          this.bubbleTimer = null;
        }
        if (this.brainwaveDisplayTimer) {
          clearInterval(this.brainwaveDisplayTimer);
          screenLifecycleStats.recordTimerClear('interval', 'brainwaveDisplayTimer');
          this.brainwaveDisplayTimer = null;
        }
        if (this.timeUpdateTimer) {
          clearInterval(this.timeUpdateTimer);
          screenLifecycleStats.recordTimerClear('interval', 'timeUpdateTimer');
          this.timeUpdateTimer = null;
        }
        if (this.welcomeMessageTimer) {
          clearInterval(this.welcomeMessageTimer);
          screenLifecycleStats.recordTimerClear('interval', 'welcomeMessageTimer');
          this.welcomeMessageTimer = null;
        }
        if (this.videoTimer) {
          clearInterval(this.videoTimer);
          screenLifecycleStats.recordTimerClear('interval', 'videoTimer');
          this.videoTimer = null;
        }
        if (this.videoPreloadTimer) {
          clearTimeout(this.videoPreloadTimer);
          screenLifecycleStats.recordTimerClear('timeout', 'videoPreloadTimer');
          this.videoPreloadTimer = null;
        }
        if (this.videoPreloadRetryTimer) {
          clearTimeout(this.videoPreloadRetryTimer);
          screenLifecycleStats.recordTimerClear('timeout', 'videoPreloadRetryTimer');
          this.videoPreloadRetryTimer = null;
        }
        if (this.cleanupPreloadVideoTimer) {
          clearTimeout(this.cleanupPreloadVideoTimer);
          screenLifecycleStats.recordTimerClear('timeout', 'cleanupPreloadVideoTimer');
          this.cleanupPreloadVideoTimer = null;
        }
        if (this.realtimeTimer) {
          clearInterval(this.realtimeTimer);
          screenLifecycleStats.recordTimerClear('interval', 'realtimeTimer');
          this.realtimeTimer = null;
        }
        if (this.sleepStateTimer) {
          clearInterval(this.sleepStateTimer);
          this.sleepStateTimer = null;
        }
        if (this.hourlyRecoveryTimer) {
          clearInterval(this.hourlyRecoveryTimer);
          this.hourlyRecoveryTimer = null;
        }
        if (this.hourlyRecoveryStarter) {
          clearTimeout(this.hourlyRecoveryStarter);
          this.hourlyRecoveryStarter = null;
        }
        if (this.midnightResetTimer) {
          clearTimeout(this.midnightResetTimer);
          this.midnightResetTimer = null;
        }
        if (this.fetchAppointmentDataRetryTimer) {
          clearTimeout(this.fetchAppointmentDataRetryTimer);
          this.fetchAppointmentDataRetryTimer = null;
        }
        if (this.fetchAppointmentDataRetryTimer2) {
          clearTimeout(this.fetchAppointmentDataRetryTimer2);
          this.fetchAppointmentDataRetryTimer2 = null;
        }
        if (this.fetchAppointmentDataDelayTimer) {
          clearTimeout(this.fetchAppointmentDataDelayTimer);
          this.fetchAppointmentDataDelayTimer = null;
        }
      }
    },

    beforeDestroy() {
      this.clearAllTimers();
    }
  };
}

